import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { formatAgo } from "../format.js";
import { useLiveShellRefresh } from "../liveRefresh.js";
import { useAsync } from "../useAsync.js";
import {
  notificationApi,
  notificationGlyph,
  notificationTargetPath,
  type NotificationItem,
  type NotificationSummary,
} from "./api.js";
import "./notifications.css";

/**
 * The bell: what has happened since you last looked.
 *
 * **One request for the whole thing.** The shell draws this on every page, so
 * it fetches a count and ten recent rows and nothing else. Mounting the
 * features a notification points at in order to populate a badge would make
 * every screen in the Hub pay for it.
 *
 * The bell is deliberately not the Inbox and not the Action Centre. It answers
 * "what happened"; the Inbox holds the correspondence somebody may want to
 * return to, and the Action Centre holds work that still needs deciding. One
 * event can produce all three, and each of them stays authoritative for its
 * own question.
 */

type NotificationBannerState = {
  item: NotificationItem;
  extra: number;
};

interface NotificationsState {
  summary: NotificationSummary | null;
  reload: () => void;
  banner: NotificationBannerState | null;
  dismissBanner: () => void;
}

const NotificationsContext = createContext<NotificationsState>({
  summary: null,
  reload: () => {},
  banner: null,
  dismissBanner: () => {},
});

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { data, reload } = useAsync(() => notificationApi.summary(), []);
  const [banner, setBanner] = useState<NotificationBannerState | null>(null);
  const knownIds = useRef<Set<string> | null>(null);
  useLiveShellRefresh(reload);

  // The first response establishes the session baseline. Only rows that appear
  // in a later live refresh get a banner, so signing in with old unread items
  // does not produce a burst of stale toasts.
  useEffect(() => {
    if (!data) return;
    const previous = knownIds.current;
    const current = new Set(data.recent.map((item) => item.id));

    if (previous) {
      const fresh = data.recent.filter(
        (item) => item.readAt === null && !previous.has(item.id),
      );
      if (fresh.length > 0) {
        setBanner({ item: fresh[0]!, extra: fresh.length - 1 });
      } else {
        // If the currently-bannered row was marked read elsewhere, do not keep
        // announcing it until somebody explicitly dismisses it.
        setBanner((shown) => {
          if (!shown) return shown;
          const latest = data.recent.find((item) => item.id === shown.item.id);
          return latest?.readAt === null ? shown : null;
        });
      }
    }

    knownIds.current = current;
  }, [data]);

  return (
    <NotificationsContext.Provider
      value={{
        summary: data,
        reload,
        banner,
        dismissBanner: () => setBanner(null),
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsState {
  return useContext(NotificationsContext);
}

function Row({
  item,
  onFollow,
}: {
  item: NotificationItem;
  onFollow: (item: NotificationItem) => void;
}) {
  return (
    <li>
      <Link
        className={`notification-row${item.readAt === null ? " is-unread" : ""}`}
        to={notificationTargetPath(item)}
        onClick={() => onFollow(item)}
      >
        <span className="notification-glyph" aria-hidden="true">
          {notificationGlyph(item.type)}
        </span>
        <span className="notification-text">
          <span className="notification-title">
            {item.readAt === null && <span aria-label="Unread">● </span>}
            {item.title}
          </span>
          {item.body && <span className="small muted notification-body">{item.body}</span>}
          <time className="small muted" dateTime={item.createdAt}>
            {formatAgo(item.createdAt)}
          </time>
        </span>
      </Link>
    </li>
  );
}

function NotificationBanner({
  state,
  onDismiss,
  onFollow,
}: {
  state: NotificationBannerState;
  onDismiss: () => void;
  onFollow: (item: NotificationItem) => void;
}) {
  const { item, extra } = state;
  return (
    <div className="notification-banner" role="status" aria-live="polite">
      <span className="notification-banner-glyph" aria-hidden="true">
        {notificationGlyph(item.type)}
      </span>
      <span className="notification-banner-copy">
        <strong>{item.title}</strong>
        {item.body && <span className="small notification-banner-body">{item.body}</span>}
        {extra > 0 && (
          <span className="small muted">
            +{extra} more new {extra === 1 ? "notification" : "notifications"}
          </span>
        )}
      </span>
      <Link
        className="notification-banner-view"
        to={notificationTargetPath(item)}
        onClick={() => {
          onDismiss();
          onFollow(item);
        }}
      >
        View
      </Link>
      <button
        type="button"
        className="quiet notification-banner-dismiss"
        aria-label="Dismiss notification"
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}

/**
 * Following a notification marks it read and goes where it points.
 *
 * Marking it read is not marking the thing it mentions read — those are
 * separate records on purpose, and the screen it lands on decides the second
 * one. It also does not decide whether the destination is reachable: a target
 * that has since been deleted or put out of reach fails there, with that
 * screen's own answer, rather than being pre-checked here.
 */
function useFollow(reload: () => void) {
  return (item: NotificationItem) => {
    if (item.readAt !== null) return;
    notificationApi
      .markRead(item.id)
      .then(() => reload())
      // A badge that stays up is a much smaller problem than a click that
      // does not navigate, so this never blocks the link.
      .catch(() => {});
  };
}

/** How wide the panel wants to be, and how close to an edge it may sit. */
const PANEL_WIDTH = 340;
const PANEL_MARGIN = 8;

/**
 * Where the panel actually goes.
 *
 * It cannot be positioned against the bell, because the bell sits inside a
 * 220px sidebar with `overflow: hidden` — a right-aligned 340px panel there
 * begins at a negative x and the heading, the start of every message and most
 * of "View all notifications" are simply cut off. On a phone the bell has the
 * navigation button to its right, which pushes it off the other edge.
 *
 * So the panel is fixed to the viewport and placed from the trigger's own
 * rectangle, clamped to stay on screen. Measured rather than guessed, because
 * the bell is in a different place above and below 900px.
 */
export function panelPosition(
  trigger: { top: number; bottom: number; right: number },
  viewport: { width: number; height: number },
): { top: number; left: number; width: number; maxHeight: number } {
  const width = Math.min(PANEL_WIDTH, viewport.width - PANEL_MARGIN * 2);
  const left = Math.min(
    Math.max(trigger.right - width, PANEL_MARGIN),
    viewport.width - width - PANEL_MARGIN,
  );
  const top = trigger.bottom + 6;
  return {
    top,
    left,
    width,
    maxHeight: Math.max(160, viewport.height - top - PANEL_MARGIN),
  };
}

export function NotificationBell() {
  const { summary, reload, banner, dismissBanner } = useNotifications();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [position, setPosition] = useState<ReturnType<typeof panelPosition> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const follow = useFollow(reload);

  // Re-measured while it is open, so a rotation or a resize cannot leave the
  // panel anchored to where the bell used to be.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition(
        panelPosition(rect, { width: window.innerWidth, height: window.innerHeight }),
      );
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    function dismissOutside(event: PointerEvent) {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", dismissOutside);
    return () => document.removeEventListener("pointerdown", dismissOutside);
  }, [open]);

  // Escape belongs to the open popup, not only to the button that opened it.
  // Once somebody tabs into a notification or the footer link, the trigger no
  // longer receives keyboard events; listening while the panel is open keeps
  // the standard dismissal behaviour available from every control inside it.
  useEffect(() => {
    if (!open) return;
    function dismissEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
    document.addEventListener("keydown", dismissEscape);
    return () => document.removeEventListener("keydown", dismissEscape);
  }, [open]);

  // Opening it is still an explicit request for the freshest contents. The
  // shared live cadence keeps the closed badge current between those clicks.
  function toggle() {
    setOpen((current) => {
      if (!current) reload();
      return !current;
    });
  }

  async function markAll() {
    if (busy) return;
    setBusy(true);
    try {
      await notificationApi.markAllRead();
      dismissBanner();
      reload();
    } catch {
      // Nothing useful to say: the badge is still right next time it loads.
    } finally {
      setBusy(false);
    }
  }

  const unread = summary?.unread ?? 0;
  const recent = summary?.recent ?? [];

  return (
    <div className="notification-bell" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="quiet notification-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={
          unread === 0
            ? "Notifications"
            : `Notifications, ${unread} unread`
        }
        onClick={toggle}
      >
        <svg
          className="notification-bell-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && (
          <span className="notification-count" aria-hidden="true">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {banner && (
        <NotificationBanner
          state={banner}
          onDismiss={dismissBanner}
          onFollow={follow}
        />
      )}

      {open && (
        <div
          className="panel notification-panel"
          id={panelId}
          style={
            position
              ? {
                  top: position.top,
                  left: position.left,
                  width: position.width,
                  maxHeight: position.maxHeight,
                }
              : undefined
          }
        >
          <div className="spread-center notification-panel-head">
            <strong>Notifications</strong>
            {unread > 0 && (
              <button type="button" className="quiet small" disabled={busy} onClick={() => void markAll()}>
                Mark all as read
              </button>
            )}
          </div>

          {recent.length === 0 ? (
            <p className="empty">Nothing has happened yet.</p>
          ) : (
            <ul className="notification-rows">
              {recent.map((item) => (
                <Row key={item.id} item={item} onFollow={follow} />
              ))}
            </ul>
          )}

          <div className="notification-panel-foot">
            <Link to="/notifications">View all notifications</Link>
          </div>
        </div>
      )}
    </div>
  );
}

/** The full history: the same rows, paged, with no conversation UX of its own. */
export function Notifications() {
  const { reload: reloadSummary } = useNotifications();
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [page, setPage] = useState(1);
  const [nonce, setNonce] = useState(0);
  const history = useAsync(() => notificationApi.list(filter, page), [filter, page, nonce]);
  const follow = useFollow(() => {
    reloadSummary();
    setNonce((value) => value + 1);
  });

  const data = history.data;

  return (
    <section aria-labelledby="notifications-heading">
      <div className="spread-center">
        <h1 id="notifications-heading">Notifications</h1>
        <button
          type="button"
          className="quiet small"
          onClick={() => {
            void notificationApi
              .markAllRead()
              .then(() => {
                reloadSummary();
                setNonce((value) => value + 1);
              })
              .catch(() => {});
          }}
        >
          Mark all as read
        </button>
      </div>

      <div className="panel">
        <div className="row" style={{ marginBottom: 12 }}>
          {(["all", "unread"] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={filter === option ? "small" : "quiet small"}
              aria-pressed={filter === option}
              onClick={() => {
                setFilter(option);
                setPage(1);
              }}
            >
              {option === "all" ? "All" : "Unread"}
            </button>
          ))}
        </div>

        {history.loading && !data && <p className="empty">Loading notifications…</p>}
        {history.error && !data && (
          <div className="error">We could not load your notifications.</div>
        )}
        {data && data.items.length === 0 && (
          <p className="empty">
            {filter === "unread" ? "Nothing unread." : "Nothing has happened yet."}
          </p>
        )}

        <ul className="notification-rows">
          {data?.items.map((item) => (
            <Row key={item.id} item={item} onFollow={follow} />
          ))}
        </ul>

        {data && data.pages > 1 && (
          <nav className="row" aria-label="Notifications pagination">
            <button
              type="button"
              className="quiet small"
              disabled={data.page <= 1}
              onClick={() => setPage(data.page - 1)}
            >
              Previous
            </button>
            <span className="small muted">
              Page {data.page} of {data.pages}
            </span>
            <button
              type="button"
              className="quiet small"
              disabled={data.page >= data.pages}
              onClick={() => setPage(data.page + 1)}
            >
              Next
            </button>
          </nav>
        )}
      </div>
    </section>
  );
}
